import React from 'react'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import styles from './ordinals-viewer.module.scss'
import useStore from 'state/knovStore'
import HiddenButton from './HiddenButton'
import ShowInProfileButton from './ShowInProfileButton'
import type { CombinedOrdinal } from 'types/ordinals'

function parseVoutFromOutpoint(outpoint: string): number | null {
    if (!outpoint) return null
    const parts = outpoint.split('_')
    if (parts.length !== 2) return null
    const v = Number(parts[1])
    if (!Number.isFinite(v)) return null
    return v
}

async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {}

    try {
        const el = document.createElement('textarea')
        el.value = text
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        el.style.top = '0'
        document.body.appendChild(el)
        el.focus()
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {}

    return false
}

interface OrdinalCardProps {
    ordinal: CombinedOrdinal
    renderPreview: (ordinal: CombinedOrdinal) => React.ReactNode
    onClick: (ordinal: CombinedOrdinal) => void
    onTreechatClick?: (ordinal: CombinedOrdinal) => void
}

const OrdinalCard: React.FC<OrdinalCardProps> = ({
    ordinal,
    renderPreview,
    onClick,
    onTreechatClick,
}) => {
    const debug = useStore(state => state.DEBUG)
    const utxoVout =
        typeof ordinal.vout === 'number' && Number.isFinite(ordinal.vout)
            ? ordinal.vout
            : parseVoutFromOutpoint(ordinal.outpoint)
    const descriptor = ordinal.descriptor || null
    const originOutpoint =
        descriptor?.origin_outpoint ||
        ordinal.inscription_outpoint ||
        ordinal.origin?.outpoint ||
        null
    const originVout = originOutpoint ? parseVoutFromOutpoint(String(originOutpoint)) : null
    const utxoOutpoint = ordinal.utxo_outpoint || ordinal.outpoint
    const ordinalId = ordinal.treechatData?.id || descriptor?.bsv_ordinal_id || null
    const showInProfile =
        !!ordinal.users_ordinal?.show_in_profile ||
        !!ordinal.treechatData?.users_ordinal?.show_in_profile
    const isHidden =
        !!ordinal.users_ordinal?.hidden || !!ordinal.treechatData?.users_ordinal?.hidden

    const handleTreechatIconClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onTreechatClick) onTreechatClick(ordinal)
    }

    const handleOpenLightboxClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement
        if (target && target.closest('[data-no-modal-interaction]')) {
            e.stopPropagation()
            e.preventDefault()
            return
        }
        onClick(ordinal)
    }

    return (
        <div className={styles.ordinalCard}>
            <div
                className={styles.previewContainer}
                onClick={handleOpenLightboxClick}
                data-testid="my-ordinal-card"
                data-bsv-ordinals-id={ordinalId ? String(ordinalId) : undefined}
                data-bsv-ordinals-origin={originOutpoint ? String(originOutpoint) : undefined}
            >
                {renderPreview(ordinal)}
                <div className={styles.statusIndicator}>
                    {ordinal.treechatData?.minted_by_treechat === true &&
                        ordinal.status === 'confirmed' && (
                            <span
                                title="Treechat Ordinal"
                                onClick={handleTreechatIconClick}
                                className={styles.treechatBadge}
                                style={{ cursor: 'pointer' }}
                            >
                                <TreechatIcon size={20} />
                            </span>
                        )}
                    {ordinal.status === 'pending' && (
                        <span title="Pending Confirmation">
                            <i className="fa fa-clock-o" />
                        </span>
                    )}
                    {ordinal.status === 'unconfirmed' && (
                        <span className={styles.unconfirmedBadge}>Pending</span>
                    )}
                </div>
            </div>
            <div className={styles.details} onClick={handleOpenLightboxClick}>
                <div className={styles.info}>
                    {ordinal.origin?.data?.map?.name || 'Untitled Ordinal'}
                </div>
                <div className={styles.type}>
                    {(ordinal.origin?.data?.insc?.file?.type || 'Unknown').split('/')[1] ||
                        'Unknown'}
                </div>
                <div className={styles.cardActions}>
                    <HiddenButton
                        ordinalId={ordinalId}
                        initialValue={isHidden}
                        className={styles.cardActionButton}
                        activeClassName={styles.cardActionButtonHiddenActive}
                        animateClassName={styles.cardActionButtonPulse}
                        showLabel={false}
                        size={14}
                    />
                    <ShowInProfileButton
                        ordinalId={ordinalId}
                        initialValue={showInProfile}
                        className={styles.cardActionButton}
                        activeClassName={styles.cardActionButtonActive}
                        animateClassName={styles.cardActionButtonPulse}
                        showLabel={false}
                        size={14}
                    />
                </div>
                {debug && !!descriptor?.bsv_vout_id && (
                    <div
                        className={styles.debugVoutSecondary}
                        title="bsv_vouts.id (click to copy)"
                        onClick={async e => {
                            e.stopPropagation()
                            e.preventDefault()
                            await copyToClipboard(String(descriptor?.bsv_vout_id))
                        }}
                    >
                        bsv_vout_id: {descriptor?.bsv_vout_id}
                    </div>
                )}
                {debug && !!descriptor?.bsv_ordinal_id && (
                    <div
                        className={styles.debugVoutSecondary}
                        title="bsv_ordinals.id (click to copy)"
                        onClick={async e => {
                            e.stopPropagation()
                            e.preventDefault()
                            await copyToClipboard(String(descriptor?.bsv_ordinal_id))
                        }}
                    >
                        bsv_ordinal_id: {descriptor?.bsv_ordinal_id}
                    </div>
                )}
                {debug && !!utxoOutpoint && (
                    <div
                        className={styles.debugVout}
                        title="Click to copy UTXO outpoint"
                        onClick={async e => {
                            e.stopPropagation()
                            e.preventDefault()
                            await copyToClipboard(String(utxoOutpoint))
                        }}
                    >
                        utxo outpoint: {utxoOutpoint}
                    </div>
                )}
                {debug && !!originOutpoint && (
                    <div
                        className={styles.debugVoutSecondary}
                        title="Click to copy inscription outpoint"
                        onClick={async e => {
                            e.stopPropagation()
                            e.preventDefault()
                            await copyToClipboard(String(originOutpoint))
                        }}
                    >
                        inscription outpoint: {originOutpoint}
                    </div>
                )}
                {debug && !!descriptor?.content_url && (
                    <div className={styles.debugVoutSecondary} title="Rails content_url (/data)">
                        data:{' '}
                        <a
                            href={String(descriptor.content_url)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => {
                                // allow navigation, but don't open the lightbox
                                e.stopPropagation()
                            }}
                        >
                            {descriptor.content_url}
                        </a>
                        <span
                            style={{
                                marginLeft: 8,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                            title="Copy URL"
                            onClick={async e => {
                                e.stopPropagation()
                                e.preventDefault()
                                await copyToClipboard(String(descriptor.content_url))
                            }}
                        >
                            copy
                        </span>
                    </div>
                )}
                {debug && !!descriptor?.preview_url && (
                    <div className={styles.debugVoutSecondary} title="preview_url">
                        preview:{' '}
                        <a
                            href={String(descriptor.preview_url)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => {
                                e.stopPropagation()
                            }}
                        >
                            {descriptor.preview_url}
                        </a>
                        <span
                            style={{
                                marginLeft: 8,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                            title="Copy URL"
                            onClick={async e => {
                                e.stopPropagation()
                                e.preventDefault()
                                await copyToClipboard(String(descriptor.preview_url))
                            }}
                        >
                            copy
                        </span>
                    </div>
                )}
                {debug && utxoVout !== null && (
                    <div
                        className={styles.debugVout}
                        title="Click to copy UTXO vout"
                        onClick={async e => {
                            e.stopPropagation()
                            e.preventDefault()
                            await copyToClipboard(String(utxoVout))
                        }}
                    >
                        utxo vout: {utxoVout}
                    </div>
                )}
                {debug && originVout !== null && originVout !== utxoVout && (
                    <div className={styles.debugVoutSecondary}>inscription vout: {originVout}</div>
                )}
            </div>
        </div>
    )
}

export default OrdinalCard
